import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, History, Search, ShoppingCart } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePosContext } from "../context/pos-context";
import { useTranslation } from "react-i18next";

export function PosHeader() {
  const { t } = useTranslation("pos");
  const {
    screen,
    isDesktop,
    searchTerm,
    setSearchTerm,
    cart,
    handleToggleCart,
    handleToggleOrderHistory,
    isCartOpen,
    isOrderHistoryOpen,
    handleBackToMenu,
    setScreen,
  } = usePosContext();

  const title =
    screen === "menu"
      ? t("menu")
      : screen === "cart"
        ? t("cart")
        : screen === "payment"
          ? t("payment")
          : t("orderConfirmed");

  const showBack = screen === "cart" || screen === "payment";
  const onBack =
    screen === "cart"
      ? handleBackToMenu
      : screen === "payment"
        ? () => (isDesktop ? setScreen("menu") : setScreen("cart"))
        : undefined;
  const storeName = screen === "menu" ? t("menu") : undefined;
  const storeTagline = screen === "menu" ? t("menuDescription") : undefined;
  const showSearch = screen === "menu";
  const isStorefront = Boolean(storeName || showSearch);

  return (
    <header className="bg-white border-b flex-shrink-0">
      <div className="flex items-center justify-between px-3 md:px-4 py-3 gap-2 md:gap-4">
        <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
          {showBack && onBack ? (
            <Button
              variant="outline"
              size="icon"
              className="rounded-full h-9 w-9 flex-shrink-0"
              onClick={onBack}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
          ) : (
            <div className="flex flex-col min-w-0">
              <h1 className="text-base md:text-lg lg:text-xl font-bold text-primary truncate">
                Someakea
              </h1>
              <p className="text-[10px] md:text-xs text-muted-foreground">
                Restaurant
              </p>
            </div>
          )}
        </div>

        {!isStorefront && !showBack && (
          <h1 className="text-base md:text-lg font-semibold text-foreground">
            {title}
          </h1>
        )}

        <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
          {showSearch && (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-9 w-24 sm:w-32 md:w-48 lg:w-64 rounded-lg border-gray-200 text-sm"
              />
            </div>
          )}
          <Button
            variant="outline"
            size="icon"
            className={cn(
              "rounded-full h-9 w-9 md:h-10 md:w-10 relative flex-shrink-0",
              isOrderHistoryOpen && "bg-primary/10 border-primary"
            )}
            onClick={handleToggleOrderHistory}
          >
            <History className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className={cn(
              "rounded-full h-9 w-9 md:h-10 md:w-10 relative flex-shrink-0",
              isCartOpen && "bg-primary/10 border-primary"
            )}
            onClick={handleToggleCart}
          >
            <ShoppingCart className="h-4 w-4 md:h-5 md:w-5" />
            {cart.itemCount > 0 && (
              <Badge
                variant="default"
                className="absolute -top-1 -right-1 md:-top-2 md:-right-2 h-4 w-4 md:h-5 md:w-5 flex items-center justify-center p-0 text-[9px] md:text-[10px]"
              >
                {cart.itemCount > 99 ? "99" : cart.itemCount}
              </Badge>
            )}
          </Button>
        </div>
      </div>
    </header>
  );
}
